import { useMemo, useState } from 'react';
import {
  OPEN_RULE_STATUSES,
  RULE_SUGGESTION_STATUSES,
  RULE_SUGGESTION_STATUS_LABELS,
  type Rule,
  type RuleSuggestion,
  type RuleSuggestionStatus,
} from '@gl/shared';

import {
  useAddSuggestionComment,
  useDeleteSuggestion,
  useDeleteSuggestionComment,
  useRuleSuggestions,
  useRules,
  useSaveSuggestion,
  useSetSuggestionStatus,
} from '../api/hooks.js';
import { useCan, useCurrentUser } from '../auth.js';
import { CollapsibleSection } from '../components/CollapsibleSection.js';
import { EmptyState, ErrorState, Loading } from '../components/states.js';
import { Markdown } from '../components/Markdown.js';
import { Modal } from '../components/Modal.js';
import { formatDate } from '../util/format.js';

/* Status is a scoreboard chip: solid, square, and readable across the room. */
const STATUS_STYLES: Record<RuleSuggestionStatus, string> = {
  awaiting_review: 'border border-field-600 text-field-300',
  in_progress: 'bg-field-100 text-field-950',
  polling: 'bg-brass-400 text-field-950',
  approved: 'bg-brass-400 text-field-950',
  denied: 'bg-signal text-white',
  impossible: 'border border-field-700 text-field-500',
};

function SuggestionCard({ suggestion }: { suggestion: RuleSuggestion }) {
  const user = useCurrentUser();
  const canEditRules = useCan('edit_rules');
  const setStatus = useSetSuggestionStatus();
  const addComment = useAddSuggestionComment();
  const deleteComment = useDeleteSuggestionComment();
  const remove = useDeleteSuggestion();
  const save = useSaveSuggestion();

  const [comment, setComment] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const isOwner = user?.id === suggestion.suggestedById;
  const canEditText = canEditRules || (isOwner && suggestion.status === 'awaiting_review');

  return (
    <article className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="whitespace-pre-wrap text-[0.9375rem] leading-relaxed text-field-100">
            {suggestion.description}
          </p>
          <p className="mt-2 font-display text-xs font-semibold uppercase tracking-bug text-field-500">
            {suggestion.suggestedByName} · {formatDate(suggestion.suggestedAt)}
          </p>
        </div>
        <span className={`badge shrink-0 ${STATUS_STYLES[suggestion.status]}`}>
          {RULE_SUGGESTION_STATUS_LABELS[suggestion.status]}
        </span>
      </div>

      {suggestion.comments.length > 0 ? (
        <ul className="mt-4 space-y-2 border-l-2 border-brass-400 pl-4">
          {suggestion.comments.map((entry) => (
            <li key={entry.id} className="text-sm">
              <p className="text-field-200">{entry.body}</p>
              <p className="mt-0.5 text-xs text-field-500">
                {entry.authorName} · {formatDate(entry.createdAt)}
                {canEditRules ? (
                  <button
                    type="button"
                    className="ml-2 text-signal hover:text-white"
                    onClick={() => deleteComment.mutate({ id: suggestion.id, commentId: entry.id })}
                  >
                    delete
                  </button>
                ) : null}
              </p>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {canEditText ? (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setEditing(suggestion.description)}
          >
            Edit
          </button>
        ) : null}

        {canEditRules ? (
          <>
            <label className="sr-only" htmlFor={`status-${suggestion.id}`}>
              Status
            </label>
            <select
              id={`status-${suggestion.id}`}
              className="input w-auto py-1.5 text-sm"
              value={suggestion.status}
              onChange={(event) =>
                setStatus.mutate({
                  id: suggestion.id,
                  status: event.target.value as RuleSuggestionStatus,
                })
              }
            >
              {RULE_SUGGESTION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {RULE_SUGGESTION_STATUS_LABELS[status]}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="btn-danger ml-auto"
              onClick={() => {
                if (confirm('Delete this suggestion?')) remove.mutate(suggestion.id);
              }}
            >
              Delete
            </button>
          </>
        ) : null}
      </div>

      {canEditRules ? (
        <div className="mt-3 flex gap-2">
          <input
            className="input"
            placeholder="Add a comment…"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={!comment.trim() || addComment.isPending}
            onClick={() =>
              addComment.mutate(
                { id: suggestion.id, body: comment.trim() },
                { onSuccess: () => setComment('') },
              )
            }
          >
            Comment
          </button>
        </div>
      ) : null}

      <Modal
        open={editing !== null}
        title="Edit suggestion"
        onClose={() => setEditing(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!editing?.trim() || save.isPending}
              onClick={() =>
                save.mutate(
                  { id: suggestion.id, description: editing?.trim() ?? '' },
                  { onSuccess: () => setEditing(null) },
                )
              }
            >
              Save
            </button>
          </>
        }
      >
        <textarea
          className="input min-h-[10rem]"
          value={editing ?? ''}
          onChange={(event) => setEditing(event.target.value)}
        />
        {save.error ? (
          <p className="mt-2 text-sm text-signal">{(save.error as Error).message}</p>
        ) : null}
      </Modal>
    </article>
  );
}

function SuggestModal({ onClose }: { onClose: () => void }) {
  const [description, setDescription] = useState('');
  const save = useSaveSuggestion();

  return (
    <Modal
      open
      title="Suggest a rule change"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={description.trim().length < 10 || save.isPending}
            onClick={() => save.mutate({ description: description.trim() }, { onSuccess: onClose })}
          >
            {save.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </>
      }
    >
      <label className="label" htmlFor="suggestion-body">
        What would you change, and why?
      </label>
      <textarea
        id="suggestion-body"
        className="input min-h-[10rem]"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Describe the rule change…"
      />
      <p className="mt-2 text-xs text-field-500">
        New suggestions start as “Awaiting review”. You can edit yours until a commissioner picks it
        up.
      </p>
      {save.error ? (
        <p className="mt-2 text-sm text-signal">{(save.error as Error).message}</p>
      ) : null}
    </Modal>
  );
}

function Rulebook({ rules }: { rules: Rule[] }) {
  const byCategory = useMemo(() => {
    const groups = new Map<string, Rule[]>();
    for (const rule of rules) {
      const list = groups.get(rule.category) ?? [];
      list.push(rule);
      groups.set(rule.category, list);
    }
    return [...groups.entries()];
  }, [rules]);

  if (rules.length === 0) {
    return <EmptyState>The rulebook has not been written up yet.</EmptyState>;
  }

  return (
    <div className="space-y-6">
      {byCategory.map(([category, entries]) => (
        <div key={category}>
          <h3 className="rail-title text-sm">{category}</h3>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {entries.map((rule) => (
              <div key={rule.id} className="card">
                <h4 className="font-display text-xl font-bold uppercase tracking-tight text-field-50">
                  {rule.title}
                </h4>
                <Markdown>{rule.body}</Markdown>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Suggestions in the order they were made, newest first, within whichever
 * section they landed in.
 *
 * Status is what splits the page up, and it says nothing about time — a
 * suggestion approved last week and one denied two years ago sit in different
 * sections but read in the same order once they are there. Ties keep the order
 * the server sent, which is the same date ordering: the imported history shares
 * a date wherever a single evening produced several suggestions.
 */
function byDateSuggested(suggestions: RuleSuggestion[]): RuleSuggestion[] {
  return [...suggestions].sort(
    (a, b) => new Date(b.suggestedAt).getTime() - new Date(a.suggestedAt).getTime(),
  );
}

type SectionKey = 'open' | 'approved' | 'rejected' | 'rulebook';

/**
 * Which sections are unfolded on arrival.
 *
 * The page is mostly history: what is being decided now, and the rulebook
 * itself, are what a reader comes for, so the two settled archives of past
 * suggestions start folded away.
 */
const INITIALLY_OPEN: Record<SectionKey, boolean> = {
  open: true,
  approved: false,
  rejected: false,
  rulebook: true,
};

export function RulesPage() {
  const suggestions = useRuleSuggestions();
  const rules = useRules();
  const canSuggest = useCan('suggest_rule_changes');
  const [suggesting, setSuggesting] = useState(false);
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>(INITIALLY_OPEN);

  const toggle = (key: SectionKey) =>
    setExpanded((current) => ({ ...current, [key]: !current[key] }));

  const anyOpen = Object.values(expanded).some(Boolean);
  const setAll = (open: boolean) =>
    setExpanded({ open, approved: open, rejected: open, rulebook: open });

  const all = suggestions.data ?? [];
  const open = byDateSuggested(all.filter((s) => OPEN_RULE_STATUSES.includes(s.status)));
  const approved = byDateSuggested(all.filter((s) => s.status === 'approved'));
  const rejected = byDateSuggested(
    all.filter((s) => s.status === 'denied' || s.status === 'impossible'),
  );

  return (
    <div>
      <div className="page-head flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1">The league</p>
          <h1 className="page-title">Rules</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => setAll(!anyOpen)}>
            {anyOpen ? 'Collapse all' : 'Expand all'}
          </button>
          {canSuggest ? (
            <button type="button" className="btn-primary" onClick={() => setSuggesting(true)}>
              Suggest a change
            </button>
          ) : null}
        </div>
      </div>

      {suggestions.isPending ? <Loading /> : null}
      {suggestions.error ? <ErrorState error={suggestions.error} /> : null}

      <CollapsibleSection
        title="Under consideration"
        count={open.length}
        open={expanded.open}
        onToggle={() => toggle('open')}
      >
        {open.length === 0 ? (
          <EmptyState>Nothing is currently being reviewed.</EmptyState>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {open.map((suggestion) => (
              <SuggestionCard key={suggestion.id} suggestion={suggestion} />
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Approved changes"
        count={approved.length}
        open={expanded.approved}
        onToggle={() => toggle('approved')}
      >
        {approved.length === 0 ? (
          <EmptyState>No suggestions have been approved yet.</EmptyState>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {approved.map((suggestion) => (
              <SuggestionCard key={suggestion.id} suggestion={suggestion} />
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Denied and impossible"
        count={rejected.length}
        open={expanded.rejected}
        onToggle={() => toggle('rejected')}
      >
        {rejected.length === 0 ? (
          <EmptyState>Nothing has been turned down yet.</EmptyState>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {rejected.map((suggestion) => (
              <SuggestionCard key={suggestion.id} suggestion={suggestion} />
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="The rulebook"
        count={rules.data?.length}
        open={expanded.rulebook}
        onToggle={() => toggle('rulebook')}
      >
        {rules.isPending ? <Loading /> : null}
        {rules.error ? <ErrorState error={rules.error} /> : null}
        {rules.data ? <Rulebook rules={rules.data} /> : null}
      </CollapsibleSection>

      {suggesting ? <SuggestModal onClose={() => setSuggesting(false)} /> : null}
    </div>
  );
}
